import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ACCOUNT_TYPE } from "@/lib/saHealth";
import { FlaskConical } from "lucide-react";

export function AccountTypeBadge({ type, cid }) {
  if (!type || type === "customer") return null;
  const t = ACCOUNT_TYPE[type];
  return <Badge className={`text-[10px] ${t.cls}`} data-testid={`sa-account-type-${cid}`}>{type === "internal_test" ? <FlaskConical className="w-3 h-3 mr-1 inline" /> : null}{t.label}</Badge>;
}

// Super Admin: convert a real live tenant to/from Internal Live Test. Sandbox is a separate classification (never convertible).
export function AccountTypeControl({ client, onChanged }) {
  const [busy, setBusy] = useState(false);
  if (client.is_sandbox) return null;
  const internal = client.account_type_effective === "internal_test";
  const next = internal ? "customer" : "internal_test";
  const change = async () => {
    const msg = internal
      ? `Reclassify ${client.business_name} as a real CUSTOMER account?\n\nIt will be included in customer counts, revenue and reports again. No data changes.`
      : `Mark ${client.business_name} as an INTERNAL LIVE TEST account?\n\nIt is excluded from customer counts, revenue, billing totals and reports by default. All users, data and settings are preserved and remain usable inside the tenant.`;
    if (!window.confirm(msg)) return;
    const reason = window.prompt("Reason (recorded in audit):") || "";
    if (!reason.trim()) { toast.error("A reason is required"); return; }
    setBusy(true);
    try { await api.put(`/superadmin/clients/${client.id}/account-type`, { account_type: next, reason }); toast.success(internal ? "Now counted as a customer" : "Marked as Internal Live Test account"); onChanged?.(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Button size="sm" variant="outline" disabled={busy} onClick={change} className={internal ? "border-purple-500/40 text-purple-700" : ""} data-testid={`sa-account-type-toggle-${client.id}`}>
      <FlaskConical className="w-4 h-4 mr-1.5" /> {internal ? "Internal Test: ON" : "Mark Internal Test"}
    </Button>
  );
}

// Product-aware correction for a company filed under the wrong NEEC product (e.g. a hardware store listed as NEEC Service).
export function ProductTypeFix({ client, onChanged }) {
  const [busy, setBusy] = useState(false);
  if (client.product_type !== "neec_service" || client.tenant_id) return null;
  const fix = async () => {
    if (!window.confirm(`Re-file ${client.business_name} as a NEEC POS company?\n\nUse this only when the business is a POS / hardware store tenant that was created under NEEC Service by mistake. Service linkage fields are archived on the record; no tenant data is deleted.`)) return;
    const reason = window.prompt("Reason (recorded in audit):") || "";
    if (!reason.trim()) { toast.error("A reason is required"); return; }
    setBusy(true);
    try { await api.put(`/superadmin/clients/${client.id}/product-type`, { product_type: "neec_pos", reason }); toast.success("Company re-filed under NEEC POS"); onChanged?.(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return <Button size="sm" variant="ghost" disabled={busy} onClick={(e) => { e.stopPropagation(); fix(); }} className="text-amber-700" data-testid={`sa-product-fix-${client.id}`}>Not a Service business? Re-file as NEEC POS</Button>;
}
